import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Camera, Upload, Trash2, Image, Plus, X, Loader2, ExternalLink } from "lucide-react";
import { useReferencePhotos, useUploadReferencePhoto, useDeleteReferencePhoto } from "@/hooks/useReferencePhotos";
import { useTaskReferencePhotos, useUploadTaskReferencePhoto, useDeleteTaskReferencePhoto } from "@/hooks/useTaskReferencePhotos";
import { toast } from "@/hooks/use-toast";
import { Room, getTaskText } from "@/types/checklist";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { AirbnbRoomMappingStep } from "@/components/owner/AirbnbRoomMappingStep";
import {
  applyLabelMappingsToPhotoAssignments,
  buildInitialLabelMappings,
  labelMappingsToRecord,
  needsRoomMappingReview,
  type AirbnbLabelMapping,
} from "@/lib/airbnbRoomMapping";

interface ReferencePhotosSectionProps {
  propertyId: string;
  rooms: Room[];
  /** When set, photos are scoped to this checklist. Required for uploads. */
  checklistId?: string | null;
}

const ReferencePhotosSection = ({ propertyId, rooms, checklistId = null }: ReferencePhotosSectionProps) => {
  const { data: referencePhotosData, isLoading } = useReferencePhotos(propertyId, checklistId ?? undefined);
  const { data: taskPhotosData } = useTaskReferencePhotos(propertyId, checklistId ?? undefined);
  const referencePhotos = referencePhotosData?.list ?? [];
  const taskPhotos = taskPhotosData?.list ?? [];
  const hasLegacyFallback = referencePhotosData?.hasLegacyFallback || taskPhotosData?.hasLegacyFallback;
  const uploadPhoto = useUploadReferencePhoto();
  const deletePhoto = useDeleteReferencePhoto();
  const uploadTaskPhoto = useUploadTaskReferencePhoto();
  const deleteTaskPhoto = useDeleteTaskReferencePhoto();
  const queryClient = useQueryClient();

  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const taskFileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const roomGalleryInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [uploadingTask, setUploadingTask] = useState<string | null>(null);
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);

  // Airbnb import state
  type AirbnbStep = "url" | "rooms" | "preview" | "saving";
  type ScrapedPhoto = { url: string; roomLabel: string | null };
  const [airbnbDialogOpen, setAirbnbDialogOpen] = useState(false);
  const [airbnbUrl, setAirbnbUrl] = useState("");
  const [airbnbStep, setAirbnbStep] = useState<AirbnbStep>("url");
  const [scrapedPhotos, setScrapedPhotos] = useState<ScrapedPhoto[]>([]);
  const [labelMappings, setLabelMappings] = useState<AirbnbLabelMapping[]>([]);
  // assignments: index → roomName (empty string = skip/deselected)
  const [assignments, setAssignments] = useState<Record<number, string>>({});

  const roomNames = rooms.map((r) => r.name);

  const resetAirbnbDialog = () => {
    setAirbnbUrl("");
    setAirbnbStep("url");
    setScrapedPhotos([]);
    setLabelMappings([]);
    setAssignments({});
  };

  const goToPhotoPreview = (photos: ScrapedPhoto[], mappings: AirbnbLabelMapping[]) => {
    const labelToRoom = labelMappingsToRecord(mappings);
    setAssignments(applyLabelMappingsToPhotoAssignments(photos, labelToRoom));
    setAirbnbStep("preview");
  };

  // Step 1: fetch preview
  const handleAirbnbPreview = async () => {
    const url = airbnbUrl.trim();
    if (!url) return;
    if (!url.includes("airbnb.")) {
      toast({ title: "Invalid URL", description: "Please enter an Airbnb listing URL", variant: "destructive" });
      return;
    }
    setAirbnbStep("saving"); // reuse saving state as "loading"
    try {
      const { data, error } = await supabase.functions.invoke("scrape-airbnb", {
        body: { phase: "preview", listingUrl: url },
      });
      if (error) throw new Error(error.message);
      const result = data as { ok: boolean; error?: string; photos?: ScrapedPhoto[]; rooms?: string[] };
      if (!result.ok) throw new Error(result.error ?? "Could not fetch photos");
      const photos = result.photos ?? [];
      if (photos.length === 0) throw new Error("No room photos found in this listing.");
      const listingRooms =
        result.rooms ??
        ([...new Set(photos.map((p) => p.roomLabel).filter(Boolean))] as string[]);
      setScrapedPhotos(photos);
      const mappings = buildInitialLabelMappings(photos, roomNames, listingRooms);
      setLabelMappings(mappings);
      if (roomNames.length > 0 && needsRoomMappingReview(mappings, roomNames)) {
        setAirbnbStep("rooms");
      } else {
        goToPhotoPreview(photos, mappings);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to fetch listing";
      toast({ title: "Could not load listing", description: msg, variant: "destructive" });
      setAirbnbStep("url");
    }
  };

  // Step 2: save selected assignments
  const handleAirbnbSave = async () => {
    const selected = scrapedPhotos
      .map((p, i) => ({ url: p.url, roomName: assignments[i] ?? "" }))
      .filter((a) => a.roomName.trim() !== "");
    if (selected.length === 0) {
      toast({ title: "No photos selected", description: "Assign at least one photo to a room.", variant: "destructive" });
      return;
    }
    setAirbnbStep("saving");
    try {
      const { data, error } = await supabase.functions.invoke("scrape-airbnb", {
        body: { phase: "import", propertyId, checklistId: checklistId ?? null, assignments: selected },
      });
      if (error) throw new Error(error.message);
      const result = data as { ok: boolean; error?: string; saved?: number };
      if (!result.ok) throw new Error(result.error ?? "Import failed");
      toast({ title: "Photos imported!", description: `${result.saved} photo${result.saved !== 1 ? "s" : ""} added to your reference photos.` });
      queryClient.invalidateQueries({ queryKey: ["reference-photos", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["task-reference-photos", propertyId] });
      setAirbnbDialogOpen(false);
      resetAirbnbDialog();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Import failed";
      toast({ title: "Import failed", description: msg, variant: "destructive" });
      setAirbnbStep("preview");
    }
  };

  const getPhotosForRoom = (roomName: string) => {
    return referencePhotos.filter((p) => p.room_name === roomName) || [];
  };

  const getTaskPhotosForRoom = (roomName: string) => {
    return taskPhotos.filter((p) => p.room_name.trim() === roomName.trim()) || [];
  };

  const getTaskPhotosForTask = (roomName: string, taskText: string) => {
    return taskPhotos.filter(
      (p) => p.room_name.trim() === roomName.trim() && p.task_text.trim() === taskText.trim()
    ) || [];
  };

  const handleFileSelect = async (roomName: string, file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file", variant: "destructive" });
      return;
    }

    try {
      await uploadPhoto.mutateAsync({ propertyId, roomName, file, checklistId: checklistId ?? undefined });
      toast({ title: "Photo uploaded", description: `Photo added to ${roomName}` });
    } catch (error) {
      console.error("Upload error:", error);
      toast({ title: "Error", description: "Failed to upload photo", variant: "destructive" });
    }
  };

  const handleMultipleFilesSelect = async (roomName: string, files: FileList) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith("image/"));
    
    if (imageFiles.length === 0) {
      toast({ title: "Invalid files", description: "Please select image files", variant: "destructive" });
      return;
    }

    for (const file of imageFiles) {
      try {
        await uploadPhoto.mutateAsync({ propertyId, roomName, file, checklistId: checklistId ?? undefined });
      } catch (error) {
        console.error("Upload error:", error);
      }
    }
    
    toast({ 
      title: "Photos uploaded", 
      description: `${imageFiles.length} photo${imageFiles.length !== 1 ? 's' : ''} added to ${roomName}` 
    });
  };

  const handleDelete = async (photo: { id: string; photo_url: string }) => {
    try {
      await deletePhoto.mutateAsync({ id: photo.id, propertyId, photoUrl: photo.photo_url, checklistId: checklistId ?? undefined });
      toast({ title: "Photo deleted", description: "Reference photo removed" });
    } catch (error) {
      console.error("Delete error:", error);
      toast({ title: "Error", description: "Failed to delete photo", variant: "destructive" });
    }
  };

  const handleTaskPhotoUpload = async (roomName: string, taskText: string, file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file", variant: "destructive" });
      return;
    }

    const taskKey = `${roomName}::${taskText}`;
    setUploadingTask(taskKey);

    try {
      await uploadTaskPhoto.mutateAsync({
        propertyId,
        roomName: roomName.trim(),
        taskText: taskText.trim(),
        file,
        checklistId: checklistId ?? undefined,
      });
      toast({ title: "Photo uploaded", description: `Task reference photo saved` });
    } catch (error) {
      console.error("Task photo upload error:", error);
      toast({ title: "Error", description: "Failed to upload task photo", variant: "destructive" });
    } finally {
      setUploadingTask(null);
    }
  };

  const handleTaskPhotoDelete = async (photo: { id: string; photo_url: string }) => {
    try {
      await deleteTaskPhoto.mutateAsync({ id: photo.id, propertyId, photoUrl: photo.photo_url, checklistId: checklistId ?? undefined });
      toast({ title: "Photo deleted", description: "Task reference photo removed" });
    } catch (error) {
      console.error("Delete error:", error);
      toast({ title: "Error", description: "Failed to delete task photo", variant: "destructive" });
    }
  };

  if (rooms.length === 0) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Camera className="w-5 h-5" />
                Reference Photos
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Click a room's photo to view and manage room & task-level reference photos
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5 text-[#FF5A5F] border-[#FF5A5F]/30 hover:bg-[#FF5A5F]/5 hover:border-[#FF5A5F]/60"
              onClick={() => setAirbnbDialogOpen(true)}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Import from Airbnb
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {hasLegacyFallback && (
            <p className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2 mb-4">
              These are property-level legacy reference photos. Upload new checklist photos to override.
            </p>
          )}
          {isLoading ? (
            <p className="text-muted-foreground text-sm">Loading photos...</p>
          ) : (
            <>
              {referencePhotos.length === 0 && taskPhotos.length === 0 && (
                <p className="text-muted-foreground text-sm mb-4">No reference photos yet. Upload a room photo to set the standard.</p>
              )}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {rooms.map((room) => {
                const roomPhotos = getPhotosForRoom(room.name);
                const taskPhotoCount = getTaskPhotosForRoom(room.name).length;
                const coverPhoto = roomPhotos[0];
                
                return (
                  <div key={room.name} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-sm">{room.name}</p>
                      <div className="flex gap-1">
                        {roomPhotos.length > 0 && (
                          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                            {roomPhotos.length} room
                          </span>
                        )}
                        {taskPhotoCount > 0 && (
                          <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                            {taskPhotoCount} Photo{taskPhotoCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {coverPhoto ? (
                      <div className="space-y-2">
                        <button
                          onClick={() => setSelectedRoom(room)}
                          className="w-full relative group"
                        >
                          <img
                            src={coverPhoto.photo_url}
                            alt={`Cover for ${room.name}`}
                            className="w-full h-32 object-cover rounded-md cursor-pointer group-hover:opacity-90 transition-opacity"
                          />
                          {roomPhotos.length > 1 && (
                            <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                              +{roomPhotos.length - 1} more
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-md">
                            <span className="text-white text-sm font-medium flex items-center gap-1">
                              <Image className="w-4 h-4" />
                              View Gallery
                            </span>
                          </div>
                        </button>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => roomGalleryInputRefs.current[room.name]?.click()}
                            disabled={uploadPhoto.isPending}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Photos
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Button
                          variant="outline"
                          className="w-full h-24 border-dashed"
                          onClick={() => roomGalleryInputRefs.current[room.name]?.click()}
                          disabled={uploadPhoto.isPending}
                        >
                          <div className="flex flex-col items-center gap-1">
                            <Upload className="w-5 h-5 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Upload room photos</span>
                          </div>
                        </Button>
                        {room.tasks.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full text-xs"
                            onClick={() => setSelectedRoom(room)}
                          >
                            <Image className="w-3 h-3 mr-1" />
                            Manage task photos ({taskPhotoCount})
                          </Button>
                        )}
                      </div>
                    )}
                    
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      ref={(el) => (roomGalleryInputRefs.current[room.name] = el)}
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) {
                          handleMultipleFilesSelect(room.name, files);
                          e.target.value = "";
                        }
                      }}
                    />
                  </div>
                );
              })}
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Room Gallery Dialog */}
      <Dialog open={!!selectedRoom} onOpenChange={() => setSelectedRoom(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              {selectedRoom?.name} - Reference Photos
            </DialogTitle>
          </DialogHeader>
          
          {selectedRoom && (
            <div className="space-y-6">
              {/* Room-level photos gallery */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium">Room Photos</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => roomGalleryInputRefs.current[`dialog-${selectedRoom.name}`]?.click()}
                    disabled={uploadPhoto.isPending}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Photos
                  </Button>
                </div>
                
                {getPhotosForRoom(selectedRoom.name).length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {getPhotosForRoom(selectedRoom.name).map((photo) => (
                      <div key={photo.id} className="relative group">
                        <img
                          src={photo.photo_url}
                          alt={`Reference for ${selectedRoom.name}`}
                          className="w-full h-24 object-cover rounded-md cursor-pointer"
                          onClick={() => setLightboxPhoto(photo.photo_url)}
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDelete(photo)}
                          disabled={deletePhoto.isPending}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full h-20 border-dashed"
                    onClick={() => roomGalleryInputRefs.current[`dialog-${selectedRoom.name}`]?.click()}
                    disabled={uploadPhoto.isPending}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <Plus className="w-5 h-5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Add room reference photos</span>
                    </div>
                  </Button>
                )}
                
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  ref={(el) => (roomGalleryInputRefs.current[`dialog-${selectedRoom.name}`] = el)}
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files && files.length > 0) {
                      handleMultipleFilesSelect(selectedRoom.name, files);
                      e.target.value = "";
                    }
                  }}
                />
              </div>

              {/* Task photos section */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Task Reference Photos</p>
                
                {selectedRoom.tasks.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No tasks in this room.</p>
                ) : (
                  <div className="space-y-3">
                    {selectedRoom.tasks.map((task, index) => {
                      const taskText = getTaskText(task);
                      const taskPhotosForTask = getTaskPhotosForTask(selectedRoom.name, taskText);
                      const taskKey = `${selectedRoom.name}::${taskText}`;
                      const isUploading = uploadingTask === taskKey;
                      
                      return (
                        <div key={index} className="border rounded-lg p-3">
                          <p className="text-sm font-medium mb-2">{taskText}</p>
                          
                          {taskPhotosForTask.length > 0 ? (
                            <div className="space-y-2">
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                {taskPhotosForTask.map((photo) => (
                                  <div key={photo.id} className="relative group">
                                    <img
                                      src={photo.photo_url}
                                      alt={`Reference for ${taskText}`}
                                      className="w-full h-16 object-cover rounded-md cursor-pointer"
                                      onClick={() => setLightboxPhoto(photo.photo_url)}
                                    />
                                    <Button
                                      variant="destructive"
                                      size="icon"
                                      className="absolute top-0.5 right-0.5 w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={() => handleTaskPhotoDelete(photo)}
                                      disabled={deleteTaskPhoto.isPending}
                                    >
                                      <X className="w-2.5 h-2.5" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full border-dashed"
                                onClick={() => taskFileInputRefs.current[taskKey]?.click()}
                                disabled={isUploading}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add more photos
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full border-dashed"
                              onClick={() => taskFileInputRefs.current[taskKey]?.click()}
                              disabled={isUploading}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              {isUploading ? "Uploading..." : "Add reference photo"}
                            </Button>
                          )}
                          
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            ref={(el) => (taskFileInputRefs.current[taskKey] = el)}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleTaskPhotoUpload(selectedRoom.name, taskText, file);
                                e.target.value = "";
                              }
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={!!lightboxPhoto} onOpenChange={() => setLightboxPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black/95">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 z-10 text-white hover:bg-white/20"
            onClick={() => setLightboxPhoto(null)}
          >
            <X className="w-5 h-5" />
          </Button>
          {lightboxPhoto && (
            <img
              src={lightboxPhoto}
              alt="Full size reference photo"
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Airbnb Import Dialog */}
      <Dialog open={airbnbDialogOpen} onOpenChange={(open) => { if (!open) resetAirbnbDialog(); setAirbnbDialogOpen(open); }}>
        <DialogContent className={airbnbStep === "preview" || airbnbStep === "rooms" ? "max-w-3xl" : "max-w-md"}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-[#FF5A5F]">✦</span>
              {airbnbStep === "preview"
                ? `${scrapedPhotos.length} photos — review assignments`
                : airbnbStep === "rooms"
                  ? "Match listing rooms to your rooms"
                  : "Import from Airbnb Listing"}
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: URL input */}
          {(airbnbStep === "url" || (airbnbStep === "saving" && scrapedPhotos.length === 0)) && (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Paste your Airbnb listing URL and we'll pull all your listing photos for you to assign to rooms.
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium">Listing URL</label>
                <Input
                  placeholder="https://www.airbnb.ca/rooms/12345678"
                  value={airbnbUrl}
                  onChange={(e) => setAirbnbUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && airbnbStep === "url") handleAirbnbPreview(); }}
                  disabled={airbnbStep === "saving"}
                />
                <p className="text-xs text-muted-foreground">Make sure the listing is publicly visible.</p>
              </div>
              {airbnbStep === "saving" && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg px-3 py-2.5">
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                  <span>Fetching photos from your listing…</span>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => { setAirbnbDialogOpen(false); resetAirbnbDialog(); }} disabled={airbnbStep === "saving"}>Cancel</Button>
                <Button size="sm" onClick={handleAirbnbPreview} disabled={airbnbStep === "saving" || !airbnbUrl.trim()} className="bg-[#FF5A5F] hover:bg-[#FF5A5F]/90 text-white">
                  {airbnbStep === "saving" ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Loading…</> : "Load Photos"}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Match listing room names to checklist rooms */}
          {airbnbStep === "rooms" && (
            <div className="space-y-4 pt-1">
              <AirbnbRoomMappingStep
                mappings={labelMappings}
                propertyRoomNames={roomNames}
                onChange={setLabelMappings}
                disabled={airbnbStep === "saving"}
              />
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => setAirbnbStep("url")}>
                  Back
                </Button>
                <Button
                  size="sm"
                  onClick={() => goToPhotoPreview(scrapedPhotos, labelMappings)}
                  disabled={labelMappings.every((m) => !m.propertyRoomName)}
                  className="bg-[#FF5A5F] hover:bg-[#FF5A5F]/90 text-white"
                >
                  Continue to photos
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Preview + per-photo tweaks */}
          {(airbnbStep === "preview" || (airbnbStep === "saving" && scrapedPhotos.length > 0)) && (
            <div className="space-y-4 pt-1">
              <p className="text-sm text-muted-foreground">
                Photos are grouped by the rooms you matched. Adjust any photo or choose Skip to leave it out.
              </p>
              <div className="max-h-[55vh] overflow-y-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pr-1">
                {scrapedPhotos.map((photo, i) => {
                  const assigned = assignments[i] ?? "";
                  const selected = assigned !== "";
                  return (
                    <div key={i} className={`relative rounded-lg overflow-hidden border-2 transition-all ${selected ? "border-primary" : "border-transparent opacity-40"}`}>
                      <img src={photo.url} alt="" className="w-full h-24 object-cover" />
                      {/* Room selector */}
                      <div className="p-1.5 bg-background">
                        <select
                          className="w-full text-xs border rounded px-1.5 py-1 bg-background"
                          value={assigned}
                          onChange={(e) => setAssignments((prev) => ({ ...prev, [i]: e.target.value }))}
                          disabled={airbnbStep === "saving"}
                        >
                          <option value="">— Skip —</option>
                          {roomNames.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-muted-foreground">
                  {Object.values(assignments).filter(v => v !== "").length} of {scrapedPhotos.length} photos selected
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      roomNames.length > 0 && labelMappings.length > 0
                        ? setAirbnbStep("rooms")
                        : setAirbnbStep("url")
                    }
                    disabled={airbnbStep === "saving"}
                  >
                    Back
                  </Button>
                  <Button size="sm" onClick={handleAirbnbSave} disabled={airbnbStep === "saving"} className="bg-[#FF5A5F] hover:bg-[#FF5A5F]/90 text-white">
                    {airbnbStep === "saving" ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Saving…</> : "Save Photos"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReferencePhotosSection;
